import React, { useCallback, type SyntheticEvent } from 'react';
import { SuccessAutoDismissFlag } from '@atlassian/jira-flags';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message';
import { useIntl } from '@atlassian/jira-intl';
import { setReturnToProjectUrl } from '@atlassian/jira-navigation-apps-sidebar-project-utils';
import { useQueryParam } from '@atlassian/jira-software-router-utils';
import { QUERY_PARAM_SELECTED_ISSUE } from '../../model/routing/constants';
import type { CardId, PartialCard } from '../../model/types';
import messages from './messages';

const linkStart = '{linkStart}';
const linkEnd = '{linkEnd}';

const getIssueLink = (key: string) => `/browse/${key}`;

export type Props = {
	showCustomFiltersSettingsLink: boolean;
	id: string | number;
	cards: PartialCard[];
	customFiltersSettingsUrl: string;
	onDismissed: undefined | ((arg1: string | number) => void);
	onViewIssue: (cardId: CardId) => void;
};

const CardCreateFiltered = ({
	cards,
	id,
	onDismissed,
	onViewIssue,
	customFiltersSettingsUrl,
	showCustomFiltersSettingsLink,
}: Props) => {
	const { formatMessage } = useIntl();
	const [, setIssueKey] = useQueryParam(QUERY_PARAM_SELECTED_ISSUE);
	const onActionClick = useCallback(() => {
		setReturnToProjectUrl();
	}, []);

	const onClickSingleCard = useCallback(
		(e: SyntheticEvent) => {
			if (cards.length === 1) {
				e.preventDefault();
				onViewIssue(cards[0].id);
				setIssueKey(cards[0].key);
			}
		},
		[cards, onViewIssue, setIssueKey],
	);

	const actions = [
		...(showCustomFiltersSettingsLink
			? [
					{
						content: formatMessage(messages.settingsLink),
						href: customFiltersSettingsUrl,
						target: '_blank',
						onClick: onActionClick,
					},
				]
			: []),
	];

	const isSingle = cards.length === 1;
	if (isSingle) {
		const card = cards[0];
		const issueLinkComponent = () => (
			<a href={getIssueLink(card.key)} onClick={onClickSingleCard}>
				{card.key}
			</a>
		);

		return (
			<SuccessAutoDismissFlag
				id={id}
				onDismissed={onDismissed}
				title={formatMessage(messages.issueHiddenTitle)}
				description={
					<FormattedI18nMessage
						message={formatMessage(messages.issueHiddenDescription, {
							linkStart,
							linkEnd,
						})}
						componentsMapping={{ link: issueLinkComponent }}
					/>
				}
				actions={[
					{
						content: formatMessage(messages.issueLink),
						onClick: onClickSingleCard,
						href: getIssueLink(card.key),
					},
					...actions,
				]}
			/>
		);
	}

	return (
		<SuccessAutoDismissFlag
			id={id}
			onDismissed={onDismissed}
			title={formatMessage(messages.multipleIssuesHiddenTitle)}
			description={formatMessage(messages.multipleIssuesHiddenDescription)}
			actions={actions}
		/>
	);
};

export default CardCreateFiltered;
